import { useState, useEffect, useRef } from "react";
import { getDocs, query, where } from "firebase/firestore";
import { dbTasks } from "../firebase";
import { useUser } from "../contexts/UserContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useUserSettings } from "../contexts/UserSettingsContext";
import { TaskDoc } from "../types";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

const isNative = Capacitor.isNativePlatform();

let notificationIdCounter = 1;

/** Send a device notification — native LocalNotifications on Android, Web API on browser */
async function sendDeviceNotification(title: string, body: string): Promise<void> {
  if (isNative) {
    await LocalNotifications.schedule({
      notifications: [
        {
          title,
          body,
          id: notificationIdCounter++,
          smallIcon: "ic_stat_notify",
        },
      ],
    });
  } else if ("Notification" in window && Notification.permission === "granted") {
    new Notification(title, { body, icon: "/icons/icon-192x192.png" });
  }
}

/** Request notification permission on both native and web */
async function requestPermission(): Promise<boolean> {
  if (isNative) {
    const result = await LocalNotifications.requestPermissions();
    return result.display === "granted";
  }
  if ("Notification" in window) {
    if (Notification.permission === "default") {
      const result = await Notification.requestPermission();
      return result === "granted";
    }
    return Notification.permission === "granted";
  }
  return false;
}

interface TaskNotificationResult {
  overdueCount: number;
  dueTodayCount: number;
}

const useTaskNotifications = (): TaskNotificationResult => {
  const { userDetails } = useUser()!;
  const { addNotification } = useNotifications();
  const { settings } = useUserSettings();
  const [overdueCount, setOverdueCount] = useState(0);
  const [dueTodayCount, setDueTodayCount] = useState(0);
  const scheduledTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    return () => {
      scheduledTimers.current.forEach(clearTimeout);
      scheduledTimers.current = [];
    };
  }, []);

  useEffect(() => {
    if (!settings.notifications) return;
    if (!(settings.notificationPrefs?.tasks ?? true)) return;
    if (!userDetails?.user?.uid) return;

    const checkTasks = async () => {
      try {
        const q = query(
          dbTasks,
          where("userId", "==", userDetails.user!.uid),
          where("toggle", "==", false)
        );
        const snapshot = await getDocs(q);
        const tasks = snapshot.docs.map((d) => ({
          docId: d.id,
          ...d.data(),
        })) as TaskDoc[];

        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let overdue = 0;
        let dueToday = 0;
        const notifyTasks: { task: TaskDoc; type: "overdue" | "today" }[] = [];
        const scheduleTimedTasks: { task: TaskDoc; delayMs: number; key: string; label: string }[] = [];

        for (const task of tasks) {
          if (!task.deadline || task.toggle) continue;

          // Compare in the user's local day. Construct deadline at local midnight
          // so the diff against `today` (also local midnight) gives whole-day units.
          const [y, mo, d] = task.deadline.split("-").map(Number);
          const deadline = new Date(y, (mo || 1) - 1, d || 1, 0, 0, 0, 0);
          const diffMs = deadline.getTime() - today.getTime();
          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

          if (diffDays < 0) {
            overdue++;
            notifyTasks.push({ task, type: "overdue" });
          } else if (diffDays === 0) {
            if (task.deadlineTime) {
              const [hours, minutes] = task.deadlineTime.split(":").map(Number);
              const targetTime = new Date();
              targetTime.setHours(hours, minutes, 0, 0);
              const delayMs = targetTime.getTime() - now.getTime();

              if (delayMs > 0) {
                // Task is due later today
                dueToday++;

                // Schedule reminder-before notification
                if (task.reminderBefore) {
                  const reminderDelay = delayMs - task.reminderBefore * 60_000;
                  if (reminderDelay > 0) {
                    // Reminder time is in the future — schedule it
                    scheduleTimedTasks.push({
                      task,
                      delayMs: reminderDelay,
                      key: `${task.docId}_reminder`,
                      label: `${task.content} — in ${task.reminderBefore} min (${task.deadlineTime})`,
                    });
                  } else {
                    // Reminder time already passed but deadline hasn't — fire immediately
                    notifyTasks.push({ task, type: "today" });
                  }
                }

                scheduleTimedTasks.push({
                  task,
                  delayMs,
                  key: `${task.docId}_timed`,
                  label: `${task.content} — due now (${task.deadlineTime})`,
                });
              } else {
                // Time has passed — treat as overdue
                overdue++;
                notifyTasks.push({ task, type: "overdue" });
              }
            } else {
              // No specific time — just due today
              dueToday++;
              notifyTasks.push({ task, type: "today" });
            }
          }
        }

        setOverdueCount(overdue);
        setDueTodayCount(dueToday);

        // Deduplication via localStorage — separate keys for browser vs in-app
        const todayKey = today.toISOString().split("T")[0];
        const browserKey = `taskNotifications_${userDetails.user!.uid}_${todayKey}`;
        const inAppKey = `taskInApp_${userDetails.user!.uid}_${todayKey}`;
        const alreadyBrowser = JSON.parse(localStorage.getItem(browserKey) || "[]") as string[];
        const alreadyInApp = JSON.parse(localStorage.getItem(inAppKey) || "[]") as string[];

        const deviceGranted = await requestPermission();

        const newBrowser: string[] = [];
        const newInApp: string[] = [];

        // Immediate notifications (overdue + due-today without future time)
        for (const { task, type } of notifyTasks) {
          if (!task.docId) continue;

          const title = type === "overdue" ? "Task Overdue" : "Task Due Today";
          const body = task.deadlineTime
            ? `${task.content} (${task.deadlineTime})`
            : task.content;

          // In-app notification (separate dedup)
          if (!alreadyInApp.includes(task.docId)) {
            newInApp.push(task.docId);
            addNotification(title, body, type);
          }

          // Device notification — only on native (Android/iOS).
          // On web, the Cloud Function sends FCM push instead to avoid duplicates.
          if (isNative && deviceGranted && !alreadyBrowser.includes(task.docId)) {
            newBrowser.push(task.docId);
            sendDeviceNotification(title, body);
          }
        }

        // Scheduled timed notifications (reminders + on-time)
        scheduledTimers.current.forEach(clearTimeout);
        scheduledTimers.current = [];

        for (const { key, delayMs, label } of scheduleTimedTasks) {
          const timer = setTimeout(() => {
            // In-app
            const currentInApp = JSON.parse(localStorage.getItem(inAppKey) || "[]") as string[];
            if (!currentInApp.includes(key)) {
              addNotification("Task Reminder", label, "reminder");
              localStorage.setItem(inAppKey, JSON.stringify([...currentInApp, key]));
            }

            // Device — only on native (Cloud Function handles web push)
            if (isNative && deviceGranted) {
              const currentBrowser = JSON.parse(localStorage.getItem(browserKey) || "[]") as string[];
              if (!currentBrowser.includes(key)) {
                sendDeviceNotification("Task Reminder", label);
                localStorage.setItem(browserKey, JSON.stringify([...currentBrowser, key]));
              }
            }
          }, delayMs);

          scheduledTimers.current.push(timer);
        }

        if (newBrowser.length > 0) {
          localStorage.setItem(browserKey, JSON.stringify([...alreadyBrowser, ...newBrowser]));
        }
        if (newInApp.length > 0) {
          localStorage.setItem(inAppKey, JSON.stringify([...alreadyInApp, ...newInApp]));
        }
      } catch (err) {
        console.error("Error checking task notifications:", err);
      }
    };

    checkTasks();

    // Re-check every 60 seconds for tasks that cross into overdue or timed range
    const interval = setInterval(checkTasks, 60_000);

    return () => {
      clearInterval(interval);
      scheduledTimers.current.forEach(clearTimeout);
      scheduledTimers.current = [];
    };
  }, [userDetails, settings.notifications, settings.notificationPrefs, addNotification]);

  return { overdueCount, dueTodayCount };
};

export default useTaskNotifications;
